
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Calendar, Calculator, Info } from "lucide-react";

interface DefaultBillingConfigProps {
  selectedType: 'per_session' | 'monthly';
  onTypeChange: (type: 'per_session' | 'monthly') => void;
  isCreating: boolean;
}

export const DefaultBillingConfig = ({ 
  selectedType, 
  onTypeChange, 
  isCreating 
}: DefaultBillingConfigProps) => {
  return (
    <Card className="border-blue-200 bg-blue-50/50">
      <CardHeader>
        <CardTitle className="text-blue-800 flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          {isCreating ? 'Configuración de facturación por defecto' : 'Configuración de facturación'}
        </CardTitle>
        {isCreating && (
          <p className="text-sm text-blue-700">
            Esta configuración se aplicará como predeterminada cuando inscribas al estudiante en actividades
          </p>
        )}
      </CardHeader>
      <CardContent>
        <RadioGroup value={selectedType} onValueChange={onTypeChange}>
          <div className="flex items-center space-x-2 p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors">
            <RadioGroupItem value="monthly" id="monthly" />
            <div className="flex-1">
              <Label htmlFor="monthly" className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">Cuota mensual</span>
                    <Badge variant="secondary" className="text-xs">Recomendado</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Cuota fija mensual, independiente de la asistencia
                  </p>
                </div>
              </Label>
            </div>
          </div>

          <div className="flex items-center space-x-2 p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors">
            <RadioGroupItem value="per_session" id="per_session" />
            <div className="flex-1">
              <Label htmlFor="per_session" className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    <span className="font-medium">Pago por día</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pagas solo por las sesiones a las que asista
                  </p>
                </div>
              </Label>
            </div>
          </div>
        </RadioGroup>

        <div className="mt-4 p-3 bg-blue-100 rounded-md">
          <div className="flex gap-2">
            <Info className="w-4 h-4 text-blue-800 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-800">
              <p className="font-medium">Información importante:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Esta configuración puede modificarse individualmente para cada actividad</li>
                <li>La facturación mensual ofrece mayor estabilidad en el presupuesto familiar</li>
                <li>El pago por día es ideal si la asistencia es irregular</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
